import { useState, useCallback, useEffect, useRef } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { supabase } from '@/lib/supabase';
import financePlaidService from '@/services/financePlaid';
import financeQuickBooksService, { QbConnection } from '@/services/financeQuickBooks';
import financeWarpService, { WarpConnectionStatus, WarpWorker } from '@/services/financeWarp';
import githubService, { GithubConnection, GithubRepo } from '@/services/github';
import googleAdsService, {
  GoogleAdsConnectionStatus,
  GoogleAdsCustomer,
} from '@/services/googleAds';
import pipedriveService, { PipedriveConnectionStatus } from '@/services/pipedrive';
import searchConsoleService, { GoogleConnectionStatus } from '@/services/searchConsole';
import seoAhrefsService, { AhrefsConnectionStatus } from '@/services/seoAhrefs';
import smartleadService, { SmartleadConnectionStatus } from '@/services/smartlead';
import { FetchApiIds } from '@/types';
import { FxBankConnection } from '@/types/api/finance';
import IntegrationsComponent from './Integrations.component';

const IntegrationsContainer = function (): JSX.Element {
  const toast = useToastContext();
  const [isConnecting, setIsConnecting] = useState(false);
  const tokenCaptured = useRef(false);

  // --- Google ---
  const {
    data: connectionStatus,
    isLoading: isLoadingConnection,
    refetch: refetchConnection,
  } = useSeoFetch<GoogleConnectionStatus>(
    [FetchApiIds.SEO_GOOGLE_CONNECTION],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (tokenCaptured.current) return;
      if (!session?.provider_token) return;
      tokenCaptured.current = true;

      try {
        const sitesRes = await fetch('https://www.googleapis.com/webmasters/v3/sites', {
          headers: { Authorization: `Bearer ${session.provider_token}` },
        });
        const sitesData = await sitesRes.json();
        const sites = sitesData?.siteEntry ?? [];

        await searchConsoleService.saveConnection({
          providerToken: session.provider_token,
          providerRefreshToken: session.provider_refresh_token ?? '',
          siteUrl: sites.length > 0 ? sites[0].siteUrl : '',
        });
        refetchConnection();
        setIsConnecting(false);
      } catch {
        toast.showErrorFeedback('Failed to capture Google token.');
        setIsConnecting(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const run = async (): Promise<void> => {
      if (tokenCaptured.current) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.provider_token) return;
      tokenCaptured.current = true;

      try {
        const sitesRes = await fetch('https://www.googleapis.com/webmasters/v3/sites', {
          headers: { Authorization: `Bearer ${session.provider_token}` },
        });
        const sitesData = await sitesRes.json();
        const sites = sitesData?.siteEntry ?? [];

        await searchConsoleService.saveConnection({
          providerToken: session.provider_token,
          providerRefreshToken: session.provider_refresh_token ?? '',
          siteUrl: sites.length > 0 ? sites[0].siteUrl : '',
        });
        refetchConnection();
      } catch {
        toast.showErrorFeedback('Failed to complete Google connection.');
      }
    };
    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = useCallback(async () => {
    setIsConnecting(true);
    tokenCaptured.current = false;
    try {
      await searchConsoleService.connectGoogle('/settings/integrations');
    } catch {
      setIsConnecting(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    try {
      await searchConsoleService.disconnect();
      tokenCaptured.current = false;
      refetchConnection();
    } catch {
      toast.showErrorFeedback('Failed to disconnect Google Search Console.');
    }
  }, [refetchConnection, toast]);

  // --- Plaid ---
  const [isPlaidConnecting, setIsPlaidConnecting] = useState(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);

  const {
    data: plaidConnections = [],
    isLoading: isLoadingPlaid,
    refetch: refetchPlaid,
  } = useSeoFetch<FxBankConnection[]>(
    [FetchApiIds.FX_BANK_CONNECTIONS],
    () => financePlaidService.getConnections(),
    { retry: false }
  );

  const handlePlaidConnect = useCallback(async () => {
    setIsPlaidConnecting(true);
    try {
      const { linkToken } = await financePlaidService.createLinkToken();
      setPlaidLinkToken(linkToken);
    } catch {
      toast.showErrorFeedback('Failed to start bank connection. Please try again.');
      setIsPlaidConnecting(false);
    }
  }, [toast]);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string) => {
      try {
        await financePlaidService.exchangeToken(publicToken);
        refetchPlaid();
      } catch {
        toast.showErrorFeedback('Failed to connect bank account. Please try again.');
      } finally {
        setIsPlaidConnecting(false);
        setPlaidLinkToken(null);
      }
    },
    [refetchPlaid, toast]
  );

  const handlePlaidExit = useCallback(() => {
    setIsPlaidConnecting(false);
    setPlaidLinkToken(null);
  }, []);

  const handlePlaidDisconnect = useCallback(
    async (connectionId: string) => {
      try {
        await financePlaidService.deleteConnection(connectionId);
        refetchPlaid();
      } catch {
        toast.showErrorFeedback('Failed to disconnect bank account.');
      }
    },
    [refetchPlaid, toast]
  );

  // --- Pipedrive ---
  const [isPipedriveConnecting, setIsPipedriveConnecting] = useState(false);
  const [pipedriveError, setPipedriveError] = useState<string | null>(null);

  const {
    data: pipedriveStatus,
    isLoading: isLoadingPipedrive,
    refetch: refetchPipedrive,
  } = useSeoFetch<PipedriveConnectionStatus>(
    ['pipedrive-connection'],
    () => pipedriveService.getConnectionStatus(),
    { retry: false }
  );

  const handlePipedriveConnect = useCallback(
    async (apiToken: string) => {
      setIsPipedriveConnecting(true);
      setPipedriveError(null);
      try {
        await pipedriveService.connect(apiToken);
        refetchPipedrive();
      } catch (err: any) {
        setPipedriveError(err.message || 'Failed to connect');
      } finally {
        setIsPipedriveConnecting(false);
      }
    },
    [refetchPipedrive]
  );

  const handlePipedriveDisconnect = useCallback(async () => {
    try {
      await pipedriveService.disconnect();
      refetchPipedrive();
    } catch (err: any) {
      setPipedriveError(err.message || 'Failed to disconnect');
    }
  }, [refetchPipedrive]);

  // --- Smartlead ---
  const [isSmartleadConnecting, setIsSmartleadConnecting] = useState(false);
  const [smartleadError, setSmartleadError] = useState<string | null>(null);

  const {
    data: smartleadStatus,
    isLoading: isLoadingSmartlead,
    refetch: refetchSmartlead,
  } = useSeoFetch<SmartleadConnectionStatus>(
    ['smartlead-connection'],
    () => smartleadService.getConnectionStatus(),
    { retry: false }
  );

  const handleSmartleadConnect = useCallback(
    async (apiKey: string) => {
      setIsSmartleadConnecting(true);
      setSmartleadError(null);
      try {
        await smartleadService.connect(apiKey);
        refetchSmartlead();
      } catch (err: any) {
        setSmartleadError(err.message || 'Failed to connect');
      } finally {
        setIsSmartleadConnecting(false);
      }
    },
    [refetchSmartlead]
  );

  const handleSmartleadDisconnect = useCallback(async () => {
    try {
      await smartleadService.disconnect();
      refetchSmartlead();
    } catch (err: any) {
      setSmartleadError(err.message || 'Failed to disconnect');
    }
  }, [refetchSmartlead]);

  // --- Google Ads ---
  const {
    data: googleAdsStatus,
    isLoading: isLoadingGoogleAds,
    refetch: refetchGoogleAds,
  } = useSeoFetch<GoogleAdsConnectionStatus>(
    ['google-ads-connection'],
    () => googleAdsService.getConnectionStatus(),
    { retry: false }
  );

  const [isGoogleAdsLoading, setIsGoogleAdsLoading] = useState(false);
  const [googleAdsCustomers, setGoogleAdsCustomers] = useState<GoogleAdsCustomer[]>([]);
  const [googleAdsError, setGoogleAdsError] = useState<string | null>(null);

  const handleGoogleAdsListCustomers = useCallback(async () => {
    setIsGoogleAdsLoading(true);
    setGoogleAdsError(null);
    try {
      const customers = await googleAdsService.listCustomers();
      setGoogleAdsCustomers(customers);
    } catch (err: any) {
      setGoogleAdsError(err.message || 'Failed to list Google Ads accounts');
    } finally {
      setIsGoogleAdsLoading(false);
    }
  }, []);

  const handleGoogleAdsSelectCustomer = useCallback(
    async (customer: GoogleAdsCustomer) => {
      setIsGoogleAdsLoading(true);
      setGoogleAdsError(null);
      try {
        await googleAdsService.saveCustomer(customer.id, customer.name);
        setGoogleAdsCustomers([]);
        refetchGoogleAds();
      } catch (err: any) {
        setGoogleAdsError(err.message || 'Failed to save account');
      } finally {
        setIsGoogleAdsLoading(false);
      }
    },
    [refetchGoogleAds]
  );

  const handleGoogleAdsDisconnect = useCallback(async () => {
    try {
      await googleAdsService.disconnect();
      refetchGoogleAds();
    } catch (err: any) {
      setGoogleAdsError(err.message || 'Failed to disconnect');
    }
  }, [refetchGoogleAds]);

  const [googleAdsTestResult, setGoogleAdsTestResult] = useState<string | null>(null);

  const handleGoogleAdsTest = useCallback(async () => {
    setGoogleAdsTestResult('Testing...');
    try {
      const result = await googleAdsService.testConnection();
      setGoogleAdsTestResult(JSON.stringify(result, null, 2));
    } catch (err: any) {
      setGoogleAdsTestResult(`Error: ${err.message}`);
    }
  }, []);

  // --- GitHub ---
  const [isGithubConnecting, setIsGithubConnecting] = useState(false);
  const [githubAccessToken, setGithubAccessToken] = useState<string | null>(null);
  const [githubRepos, setGithubRepos] = useState<GithubRepo[]>([]);

  const {
    data: githubConnection,
    isLoading: isLoadingGithub,
    refetch: refetchGithub,
  } = useSeoFetch<GithubConnection | null>(
    [FetchApiIds.GITHUB_CONNECTION],
    () => githubService.getConnection(),
    { retry: false }
  );

  // Handle GitHub OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    const exchangeCode = async (): Promise<void> => {
      setIsGithubConnecting(true);
      try {
        const accessToken = await githubService.handleOAuthCallback(code, state);
        const repos = await githubService.fetchRepos(accessToken);
        setGithubAccessToken(accessToken);
        setGithubRepos(repos);
      } catch {
        toast.showErrorFeedback('Failed to complete GitHub connection.');
      } finally {
        setIsGithubConnecting(false);
      }
    };
    exchangeCode();
  }, []);

  const handleGithubConnect = useCallback(async () => {
    setIsGithubConnecting(true);
    try {
      await githubService.startOAuthFlow();
    } catch {
      toast.showErrorFeedback('Failed to start GitHub connection.');
      setIsGithubConnecting(false);
    }
  }, []);

  const handleGithubRepoSelect = useCallback(
    async (repo: GithubRepo) => {
      if (!githubAccessToken) return;
      setIsGithubConnecting(true);
      try {
        await githubService.saveConnection({
          accessToken: githubAccessToken,
          repoOwner: repo.owner,
          repoName: repo.name,
          branch: repo.defaultBranch,
        });
        setGithubAccessToken(null);
        setGithubRepos([]);
        refetchGithub();
      } catch {
        toast.showErrorFeedback('Failed to save GitHub repository.');
      } finally {
        setIsGithubConnecting(false);
      }
    },
    [githubAccessToken, refetchGithub]
  );

  const handleGithubCancelSetup = useCallback(() => {
    setGithubAccessToken(null);
    setGithubRepos([]);
  }, []);

  const handleGithubManualRepo = useCallback(
    async (owner: string, name: string) => {
      if (!githubAccessToken) return;
      setIsGithubConnecting(true);
      try {
        await githubService.saveConnection({
          accessToken: githubAccessToken,
          repoOwner: owner,
          repoName: name,
          branch: 'main',
        });
        setGithubAccessToken(null);
        setGithubRepos([]);
        refetchGithub();
      } catch {
        toast.showErrorFeedback('Failed to save GitHub repository.');
      } finally {
        setIsGithubConnecting(false);
      }
    },
    [githubAccessToken, refetchGithub]
  );

  const handleGithubPatConnect = useCallback(
    async (token: string, owner: string, name: string) => {
      setIsGithubConnecting(true);
      try {
        const isValid = await githubService.validateToken(token);
        if (!isValid) throw new Error('Invalid token');
        await githubService.saveConnection({
          accessToken: token,
          repoOwner: owner,
          repoName: name,
          branch: 'main',
        });
        refetchGithub();
      } catch (err) {
        toast.showErrorFeedback('Failed to connect with GitHub token.');
        throw err;
      } finally {
        setIsGithubConnecting(false);
      }
    },
    [refetchGithub]
  );

  const handleGithubDisconnect = useCallback(async () => {
    try {
      await githubService.disconnect();
      setGithubAccessToken(null);
      setGithubRepos([]);
      refetchGithub();
    } catch {
      toast.showErrorFeedback('Failed to disconnect GitHub.');
    }
  }, [refetchGithub]);

  // --- Ahrefs ---
  const [isAhrefsConnecting, setIsAhrefsConnecting] = useState(false);
  const [ahrefsError, setAhrefsError] = useState<string | null>(null);

  const {
    data: ahrefsStatus,
    isLoading: isLoadingAhrefs,
    refetch: refetchAhrefs,
  } = useSeoFetch<AhrefsConnectionStatus>(
    ['ahrefs-connection'],
    () => seoAhrefsService.getConnectionStatus(),
    { retry: false }
  );

  const handleAhrefsConnect = useCallback(
    async (apiKey: string) => {
      setIsAhrefsConnecting(true);
      setAhrefsError(null);
      try {
        await seoAhrefsService.connect(apiKey);
        refetchAhrefs();
      } catch (err: any) {
        setAhrefsError(err.message || 'Failed to connect');
      } finally {
        setIsAhrefsConnecting(false);
      }
    },
    [refetchAhrefs]
  );

  const handleAhrefsDisconnect = useCallback(async () => {
    try {
      await seoAhrefsService.disconnect();
      refetchAhrefs();
    } catch (err: any) {
      setAhrefsError(err.message || 'Failed to disconnect');
    }
  }, [refetchAhrefs]);

  const [isAhrefsSyncing, setIsAhrefsSyncing] = useState(false);

  const handleAhrefsSync = useCallback(async () => {
    setIsAhrefsSyncing(true);
    setAhrefsError(null);
    try {
      const { data: projects } = await supabase
        .from('seo_projects')
        .select('id, domain')
        .is('deleted_at', null);

      if (!projects?.length) {
        setAhrefsError('No SEO projects found to sync');
        return;
      }

      for (const project of projects) {
        await seoAhrefsService.syncAll(project.id);
      }
    } catch (err: any) {
      setAhrefsError(err.message || 'Sync failed');
    } finally {
      setIsAhrefsSyncing(false);
    }
  }, []);

  // --- QuickBooks ---
  const [isQbConnecting, setIsQbConnecting] = useState(false);
  const [isQbSyncing, setIsQbSyncing] = useState(false);
  const [qbSyncResult, setQbSyncResult] = useState<string | null>(null);

  const {
    data: qbConnections = [],
    isLoading: isLoadingQb,
    refetch: refetchQb,
  } = useSeoFetch<QbConnection[]>(
    ['qb-connections'],
    () => financeQuickBooksService.getConnections(),
    { retry: false }
  );

  // Handle QB OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      refetchQb();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQbConnect = useCallback(async () => {
    setIsQbConnecting(true);
    try {
      const { authUrl } = await financeQuickBooksService.getAuthUrl();
      window.location.href = authUrl;
    } catch {
      toast.showErrorFeedback('Failed to start QuickBooks connection.');
      setIsQbConnecting(false);
    }
  }, []);

  const handleQbSync = useCallback(
    async (
      connectionId: string,
      startDate?: string,
      endDate?: string,
      syncType?: 'all' | 'transactions' | 'invoices'
    ) => {
      setIsQbSyncing(true);
      setQbSyncResult(null);
      try {
        const result = await financeQuickBooksService.syncConnection(connectionId, {
          startDate,
          endDate,
          syncType,
        });
        const lines = result.linesCreated
          ? `, ${result.linesCreated} line items (${result.linesMatched} matched to profiles)`
          : '';
        const txPart = result.transactions
          ? ` | ${result.transactions.created} transactions imported${
              result.transactions.updated ? `, ${result.transactions.updated} updated` : ''
            }${
              result.transactions.transfersDetected
                ? `, ${result.transactions.transfersDetected} transfers detected`
                : ''
            }`
          : '';
        setQbSyncResult(
          `Synced ${result.synced} invoices (${result.created} new, ${result.updated} updated)${lines}${txPart}`
        );
        refetchQb();
      } catch (err: any) {
        setQbSyncResult(`Error: ${err.message}`);
      } finally {
        setIsQbSyncing(false);
      }
    },
    [refetchQb]
  );

  const handleQbDisconnect = useCallback(
    async (connectionId: string) => {
      try {
        await financeQuickBooksService.deleteConnection(connectionId);
        refetchQb();
      } catch {
        toast.showErrorFeedback('Failed to disconnect QuickBooks.');
      }
    },
    [refetchQb, toast]
  );

  // --- Warp ---
  const [isWarpConnecting, setIsWarpConnecting] = useState(false);
  const [isWarpSyncing, setIsWarpSyncing] = useState(false);
  const [isWarpLinking, setIsWarpLinking] = useState(false);
  const [warpError, setWarpError] = useState<string | null>(null);
  const [warpResult, setWarpResult] = useState<string | null>(null);
  const [warpCreateProfiles, setWarpCreateProfiles] = useState(false);

  const {
    data: warpStatus,
    isLoading: isLoadingWarp,
    refetch: refetchWarp,
  } = useSeoFetch<WarpConnectionStatus>(
    ['warp-connection'],
    () => financeWarpService.getConnectionStatus(),
    { retry: false }
  );

  const { data: warpContractors = [], refetch: refetchWarpContractors } = useSeoFetch<WarpWorker[]>(
    ['warp-contractors'],
    () => financeWarpService.getWorkers({ type: 'contractor' }),
    { retry: false }
  );

  const handleWarpConnect = useCallback(
    async (apiKey: string) => {
      setIsWarpConnecting(true);
      setWarpError(null);
      try {
        await financeWarpService.connect(apiKey);
        refetchWarp();
      } catch (err: any) {
        setWarpError(err.message || 'Failed to connect');
      } finally {
        setIsWarpConnecting(false);
      }
    },
    [refetchWarp]
  );

  const handleWarpSync = useCallback(async () => {
    setIsWarpSyncing(true);
    setWarpError(null);
    setWarpResult(null);
    try {
      const result = await financeWarpService.syncWorkers({
        createMissingProfiles: warpCreateProfiles,
      });
      const createdPart = result.profilesCreated
        ? ` · ${result.profilesCreated} profiles created`
        : '';
      const namePart = result.profilesLinkedByName
        ? ` · ${result.profilesLinkedByName} matched by name`
        : '';
      const ambigPart = result.ambiguousSkipped
        ? ` · ${result.ambiguousSkipped} ambiguous (skipped)`
        : '';
      setWarpResult(
        `Synced ${result.synced} workers (${result.contractors} contractors, ${result.employees} employees) · ${result.linkedToProfiles} linked to profiles${createdPart}${namePart}${ambigPart}`
      );
      refetchWarp();
      refetchWarpContractors();
    } catch (err: any) {
      setWarpResult(`Error: ${err.message}`);
    } finally {
      setIsWarpSyncing(false);
    }
  }, [refetchWarp, refetchWarpContractors, warpCreateProfiles]);

  const handleWarpLink = useCallback(async () => {
    setIsWarpLinking(true);
    setWarpError(null);
    setWarpResult(null);
    try {
      const result = await financeWarpService.linkToVendors();
      setWarpResult(
        `Linked ${result.linked} contractors to vendors (${result.created} new vendors created)`
      );
      refetchWarpContractors();
    } catch (err: any) {
      setWarpResult(`Error: ${err.message}`);
    } finally {
      setIsWarpLinking(false);
    }
  }, [refetchWarpContractors]);

  const handleWarpDisconnect = useCallback(async () => {
    try {
      await financeWarpService.disconnect();
      refetchWarp();
    } catch (err: any) {
      setWarpError(err.message || 'Failed to disconnect');
    }
  }, [refetchWarp]);

  return (
    <IntegrationsComponent
      connectionStatus={connectionStatus ?? null}
      isLoading={
        isLoadingConnection ||
        isLoadingPlaid ||
        isLoadingPipedrive ||
        isLoadingSmartlead ||
        isLoadingGithub ||
        isLoadingGoogleAds ||
        isLoadingAhrefs ||
        isLoadingQb ||
        isLoadingWarp
      }
      isConnecting={isConnecting}
      onConnect={handleConnect}
      onDisconnect={handleDisconnect}
      plaidConnections={plaidConnections}
      isPlaidConnecting={isPlaidConnecting}
      plaidLinkToken={plaidLinkToken}
      onPlaidConnect={handlePlaidConnect}
      onPlaidSuccess={handlePlaidSuccess}
      onPlaidExit={handlePlaidExit}
      onPlaidDisconnect={handlePlaidDisconnect}
      pipedriveStatus={pipedriveStatus ?? null}
      isPipedriveConnecting={isPipedriveConnecting}
      pipedriveError={pipedriveError}
      onPipedriveConnect={handlePipedriveConnect}
      onPipedriveDisconnect={handlePipedriveDisconnect}
      smartleadStatus={smartleadStatus ?? null}
      isSmartleadConnecting={isSmartleadConnecting}
      smartleadError={smartleadError}
      onSmartleadConnect={handleSmartleadConnect}
      onSmartleadDisconnect={handleSmartleadDisconnect}
      githubConnection={githubConnection ?? null}
      isGithubConnecting={isGithubConnecting}
      githubRepos={githubRepos}
      onGithubConnect={handleGithubConnect}
      onGithubCancelSetup={handleGithubCancelSetup}
      onGithubRepoSelect={handleGithubRepoSelect}
      onGithubManualRepo={handleGithubManualRepo}
      onGithubPatConnect={handleGithubPatConnect}
      onGithubDisconnect={handleGithubDisconnect}
      googleAdsStatus={googleAdsStatus ?? null}
      isGoogleAdsLoading={isGoogleAdsLoading}
      googleAdsCustomers={googleAdsCustomers}
      googleAdsError={googleAdsError}
      onGoogleAdsListCustomers={handleGoogleAdsListCustomers}
      onGoogleAdsSelectCustomer={handleGoogleAdsSelectCustomer}
      onGoogleAdsDisconnect={handleGoogleAdsDisconnect}
      onGoogleAdsTest={handleGoogleAdsTest}
      googleAdsTestResult={googleAdsTestResult}
      ahrefsStatus={ahrefsStatus ?? null}
      isAhrefsConnecting={isAhrefsConnecting}
      ahrefsError={ahrefsError}
      onAhrefsConnect={handleAhrefsConnect}
      onAhrefsDisconnect={handleAhrefsDisconnect}
      isAhrefsSyncing={isAhrefsSyncing}
      onAhrefsSync={handleAhrefsSync}
      qbConnections={qbConnections}
      isQbConnecting={isQbConnecting}
      isQbSyncing={isQbSyncing}
      qbSyncResult={qbSyncResult}
      onQbConnect={handleQbConnect}
      onQbSync={handleQbSync}
      onQbDisconnect={handleQbDisconnect}
      warpStatus={warpStatus ?? null}
      warpContractors={warpContractors}
      isWarpConnecting={isWarpConnecting}
      isWarpSyncing={isWarpSyncing}
      isWarpLinking={isWarpLinking}
      warpError={warpError}
      warpResult={warpResult}
      warpCreateProfiles={warpCreateProfiles}
      onWarpCreateProfilesChange={setWarpCreateProfiles}
      onWarpConnect={handleWarpConnect}
      onWarpSync={handleWarpSync}
      onWarpLink={handleWarpLink}
      onWarpDisconnect={handleWarpDisconnect}
    />
  );
};

export default IntegrationsContainer;
